import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePageMeta } from '@/hooks/usePageMeta';
import { usePublicCopy } from '@/i18n/publicCopy';
import { PublicHeader } from '@/components/public/PublicHeader';
import { PublicFooter } from '@/components/public/PublicFooter';
import { Hero, ThreePaths, FreeEssentials, HowItWorks, PrivacyScope, Faq } from '@/components/public/HomeSections';
import { BlogTeaser } from '@/components/public/BlogTeaser';
import { CommunitySpotlight } from '@/components/public/CommunitySpotlight';

/** Public homepage — six short sections (hero · three paths · free essentials · how it works · privacy & scope · FAQ) + footer. */
export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { c } = usePublicCopy();
  const h = c.home;
  usePageMeta({ title: h.meta_title, description: h.meta_desc, path: '/' });

  useEffect(() => {
    try {  // referral codes still work when a visitor lands here first
      const ref = (new URLSearchParams(window.location.search).get('ref') || '').trim();
      if (ref) { localStorage.setItem('sahiri_ref_code', ref); localStorage.setItem('sahiri_ref_code_ts', String(Date.now())); }
    } catch { /* storage unavailable */ }
    if (user && !new URLSearchParams(window.location.search).has('celebrate')) { navigate('/dashboard', { replace: true }); return; }
    const id = window.location.hash.replace('#', '');
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  }, [user, navigate]);

  const start = () => navigate('/values');

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="home-page">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-4 focus:left-4 focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm focus:text-white" style={{ backgroundColor: 'var(--accent)' }}>Skip to main content</a>
      <PublicHeader />
      <main>
        <Hero c={h} onStart={start} />
        <ThreePaths c={h} />
        <FreeEssentials c={h} />
        <CommunitySpotlight />
        <HowItWorks c={h} />
        <PrivacyScope c={h} />
        <BlogTeaser />
        <Faq c={h} />
        <section className="px-5 md:px-8 py-16 border-t" style={{ borderColor: 'var(--border)' }} aria-label={h.cta_h2}>
          <div className="max-w-6xl mx-auto flex flex-wrap items-end justify-between gap-6">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight mb-2" style={{ color: 'var(--text-primary)' }}>{h.cta_h2}</h2>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{h.cta_p}</p>
            </div>
            <button type="button" onClick={start} className="px-7 py-3.5 rounded-full text-sm font-medium text-white transition-transform hover:scale-[1.02]" style={{ backgroundColor: 'var(--accent)' }} data-testid="home-final-cta">{h.hero_primary}</button>
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
